/**
 * PolicyDetailPage — /agents/libraries/model-runtime-policies/:id
 * One runtime policy on a DetailPage: URL-synced tabs, one form across the editing tabs, Save and Cancel below.
 * Demo example: /profile (tabbed detail) + /form-layouts; NextAdmin `profile/layout.tsx`.
 */
import { Copy, Cpu, DollarSign, Info, Layers, Lock, Route, SlidersHorizontal, Target } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getModelRuntimePolicyOptions,
  updateModelRuntimePolicyMutation,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import type { RuntimePolicyRetireCounts } from '../../../generated/api/types.gen.js';
import { Can } from '../../../components/auth/Can.js';
import { Form, FormActions, useZodForm } from '../../../components/form/index.js';
import { DetailPage } from '../../../components/page/DetailPage.js';
import { buttonStyles } from '../../../components/tailgrids/core/button.js';
import type { TabItem } from '../../../components/ui/index.js';
import { Badge, Callout, Card, QueryBoundary, useToast } from '../../../components/ui/index.js';
import { usePolicy } from '../../../hooks/usePolicy.js';
import { useTabParam } from '../../../hooks/useTabParam.js';
import { PolicyStatusBadge, RiskBadge } from './PolicyCatalogList.js';
import { LimitsSection, PermissionsSection, ProviderSection } from './PolicyEditorSections.js';
import { IdentitySection, RoutingSection } from './PolicyEditorSectionsA.js';
import { PolicyModelsSection } from './PolicyModelsSection.js';
import { ResolverPreviewSection } from './ResolverPreviewSection.js';
import { POLICIES_PATH, asApiError, useRefreshPolicies } from './policy-api.js';
import type { ModelRuntimePolicy, PolicyFormOutput, PolicyTabId } from './types.js';
import { FIELD_TAB, formValuesToPayload, humanizeEnum, policyFormSchema, policyToFormValues } from './types.js';

const TABS: readonly (TabItem & { id: PolicyTabId })[] = [
  { id: 'identity', label: 'Identity', icon: Info },
  { id: 'models', label: 'Models', icon: Layers },
  { id: 'routing', label: 'Routing intent', icon: Route },
  { id: 'limits', label: 'Limits', icon: SlidersHorizontal },
  { id: 'permissions', label: 'Permissions', icon: Lock },
  { id: 'provider', label: 'Provider', icon: Cpu },
  { id: 'resolve', label: 'Resolver preview', icon: Target },
];
const TAB_IDS = TABS.map((tab) => tab.id);
const TAB_LABEL = new Map(TABS.map((tab) => [tab.id, tab.label]));

function PolicyEditor({ policy }: { policy: ModelRuntimePolicy }) {
  const { notify } = useToast();
  const refresh = useRefreshPolicies();
  const canEdit = usePolicy('CanManageSystem');
  const [tab, setTab] = useTabParam<PolicyTabId>(TAB_IDS, 'identity');
  // Bumped on Cancel and after a save so the JSON field, which keeps its own text, shows the form's values again.
  const [revision, setRevision] = useState(0);
  const [retired, setRetired] = useState<RuntimePolicyRetireCounts | null>(null);
  const update = useMutation(updateModelRuntimePolicyMutation());
  const form = useZodForm(policyFormSchema, { defaultValues: policyToFormValues(policy) });

  const save = async (values: PolicyFormOutput) => {
    if (!form.formState.isDirty) {
      notify('No changes to save', 'info');
      return;
    }
    const result = await update
      .mutateAsync({ path: { id: policy.id }, body: formValuesToPayload(values) })
      .catch((error: unknown) => {
        throw asApiError(error);
      });
    setRetired(result.retired ?? null);
    await refresh(policy.id);
    form.reset(policyToFormValues(result.data));
    setRevision((n) => n + 1);
    notify('Policy saved');
  };

  const cancel = () => {
    form.reset(policyToFormValues(policy));
    setRetired(null);
    setRevision((n) => n + 1);
  };

  // A save blocked by a field on a tab that is not showing: name the tabs, so the problem is findable.
  const failingTabs = [...new Set(Object.keys(form.formState.errors).map((field) => FIELD_TAB[field]))].filter(
    (id): id is PolicyTabId => id !== undefined,
  );
  const hiddenFailingTabs = failingTabs.filter((id) => id !== tab);

  const sectionProps = { isCreating: false, canEdit };
  const sections: Record<Exclude<PolicyTabId, 'resolve'>, React.ReactNode> = {
    identity: <IdentitySection {...sectionProps} />,
    models: <PolicyModelsSection {...sectionProps} policyId={policy.id} />,
    routing: <RoutingSection {...sectionProps} />,
    limits: <LimitsSection {...sectionProps} />,
    permissions: <PermissionsSection {...sectionProps} />,
    provider: <ProviderSection {...sectionProps} />,
  };

  return (
    <DetailPage
      title={policy.name}
      subtitle={`${policy.slug}@${policy.version} · ${policy.scope === 'system' ? 'System' : 'Organization'} policy · ${humanizeEnum(policy.purpose)}`}
      breadcrumbs={[
        { label: 'Libraries', to: '/agents/libraries' },
        { label: 'Runtime policies', to: POLICIES_PATH },
      ]}
      badges={
        <>
          <PolicyStatusBadge status={policy.status} />
          <RiskBadge tier={policy.riskTier} />
          <Badge variant="neutral" icon={<Lock size={12} aria-hidden="true" />}>
            {humanizeEnum(policy.permissionMode)}
          </Badge>
          {policy.spendCapUsd != null && (
            <Badge variant="neutral" icon={<DollarSign size={12} aria-hidden="true" />}>
              ${policy.spendCapUsd} cap
            </Badge>
          )}
        </>
      }
      actions={
        <>
          <Link to={POLICIES_PATH} className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}>
            All policies
          </Link>
          <Can policy="CanManageSystem">
            <Link
              to={`${POLICIES_PATH}/new?from=${policy.id}`}
              className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}
            >
              <Copy size={14} aria-hidden="true" />
              Duplicate
            </Link>
          </Can>
        </>
      }
      alert={
        <>
          {!canEdit && (
            <Callout tone="purple" icon={Lock}>
              Editing needs system management access. You can view runtime policies but not change them.
            </Callout>
          )}
          {retired && (
            <Callout tone="warning" title="Policy retired">
              Moved to the defaults: {retired.conversationsMoved} conversation(s), {retired.entitiesMoved} other
              item(s). Removed from {retired.allowedRemoved} allowed list(s); cleared as fallback in{' '}
              {retired.fallbacksCleared} polic{retired.fallbacksCleared === 1 ? 'y' : 'ies'}.
            </Callout>
          )}
          {hiddenFailingTabs.length > 0 && (
            <Callout tone="danger" title="Fix these fields before saving">
              Check the {hiddenFailingTabs.map((id) => TAB_LABEL.get(id)).join(', ')} tab
              {hiddenFailingTabs.length === 1 ? '' : 's'}.
            </Callout>
          )}
        </>
      }
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === 'resolve' ? (
        <ResolverPreviewSection />
      ) : (
        <Form form={form} onSubmit={save} guardUnsaved>
          <Card>
            <div key={revision} className="flex flex-col gap-8">
              {sections[tab]}
            </div>
          </Card>
          {canEdit && <FormActions submitLabel="Save changes" onCancel={cancel} />}
        </Form>
      )}
    </DetailPage>
  );
}

export const PolicyDetailPage: React.FC = () => {
  const id = Number(useParams().id);
  const valid = Number.isInteger(id) && id > 0;
  const query = useQuery({ ...getModelRuntimePolicyOptions({ path: { id } }), enabled: valid });

  if (!valid) return <Navigate to={POLICIES_PATH} replace />;

  return (
    <QueryBoundary query={query} label="this runtime policy">
      {(response) => <PolicyEditor key={response.data.id} policy={response.data} />}
    </QueryBoundary>
  );
};
